import { useState } from 'react'
import { Check, ChevronRight, ClipboardCopy, Loader2, Printer, Sparkles } from 'lucide-react'
import { formatNumber } from '../../lib/utils'
import { useMunicipalityLageprompt } from '../../hooks/queries'
import { printLagebeschreibung } from './lagePrint'
import { LageSectionBoxes } from './LageSectionBoxes'
import type { Municipality, MunicipalitySnapshot } from '../../types/api'

interface OverviewTabProps {
  municipality: Municipality
  municipalityKey: string
  currentSnapshot: MunicipalitySnapshot | undefined
  getTextkennzeichenName: (code: string | null | undefined) => string
  onNavigateTab: (tab: 'kreis' | 'bundesland' | 'bund') => void
}

export default function OverviewTab({
  municipality,
  municipalityKey,
  currentSnapshot,
  getTextkennzeichenName,
  onNavigateTab,
}: OverviewTabProps) {
  const { data: lageprompt, isLoading: lageLoading } = useMunicipalityLageprompt(municipalityKey)
  const [promptCopied, setPromptCopied] = useState(false)

  const handleCopyPrompt = async () => {
    if (!lageprompt?.prompt) return
    await navigator.clipboard.writeText(lageprompt.prompt)
    setPromptCopied(true)
    setTimeout(() => setPromptCopied(false), 2000)
  }

  return (
    <div className="space-y-6">
      {/* Kopfbereich: Stammdaten (links) + Aktionen & Verwaltungsebenen (rechts) */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Basic Info Card - Primary Dark */}
        <div className="lg:col-span-2 card-dark">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-text-inverse">Gemeindeverzeichnis</h3>
            {currentSnapshot?.data_source && (
              <span className="text-xs text-primary bg-text-inverse/10 px-2 py-1 rounded-gut8">
                Edition {currentSnapshot.data_source.edition} · {currentSnapshot.data_source.reference_date_display}
              </span>
            )}
          </div>
          <dl className="grid grid-cols-2 gap-4">
            <div className="space-y-1">
              <dt className="text-xs font-semibold text-primary uppercase tracking-wider">AGS (8-stellig)</dt>
              <dd className="text-text-inverse font-mono">{municipality.ags}</dd>
            </div>
            <div className="space-y-1">
              <dt className="text-xs font-semibold text-primary uppercase tracking-wider">ARS (12-stellig)</dt>
              <dd className="text-text-inverse font-mono">{municipality.ars}</dd>
            </div>
            <div className="col-span-2 space-y-1">
              <dt className="text-xs font-semibold text-primary uppercase tracking-wider">Name</dt>
              <dd className="text-text-inverse">
                {currentSnapshot ? currentSnapshot.name : municipality.name}
                {municipality.name_addon && <span className="text-text-inverse/60 ml-1">({municipality.name_addon})</span>}
              </dd>
            </div>
            <div className="space-y-1">
              <dt className="text-xs font-semibold text-primary uppercase tracking-wider">Einwohner</dt>
              <dd className="text-text-inverse">
                {formatNumber(currentSnapshot ? currentSnapshot.population : municipality.population)}
              </dd>
            </div>
            <div className="space-y-1">
              <dt className="text-xs font-semibold text-primary uppercase tracking-wider">Fläche</dt>
              <dd className="text-text-inverse">
                {(() => {
                  const area = currentSnapshot ? currentSnapshot.area_km2 : municipality.area_km2
                  return area ? `${area.toFixed(2)} km²` : 'N/A'
                })()}
              </dd>
            </div>
            <div className="space-y-1">
              <dt className="text-xs font-semibold text-primary uppercase tracking-wider">Bevölkerungsdichte</dt>
              <dd className="text-text-inverse">
                {(() => {
                  const density = currentSnapshot ? currentSnapshot.population_density : municipality.population_density
                  return density ? `${density.toFixed(0)} Einw./km²` : 'N/A'
                })()}
              </dd>
            </div>
            {municipality.postal_code && (
              <div className="space-y-1">
                <dt className="text-xs font-semibold text-primary uppercase tracking-wider">Postleitzahl</dt>
                <dd className="text-text-inverse">{municipality.postal_code}</dd>
              </div>
            )}
          </dl>

          {/* NAD Classifications */}
          {(municipality.travel_region || municipality.urbanization_area ||
            municipality.municipality_type_classification || municipality.urban_rural_type || municipality.centrality_type) && (
            <>
              <h4 className="text-lg font-semibold mt-6 mb-3 text-text-inverse/80 border-t border-text-inverse/10 pt-4">
                NAD-Klassifikationen
              </h4>
              <dl className="grid grid-cols-2 gap-4">
                {municipality.travel_region && (
                  <div className="space-y-1">
                    <dt className="text-xs font-semibold text-primary uppercase tracking-wider">Reisegebiet</dt>
                    <dd className="text-text-inverse">
                      {municipality.travel_region.name}
                      {municipality.travel_region.description && (
                        <span className="text-text-inverse/60 text-xs block">{municipality.travel_region.description}</span>
                      )}
                    </dd>
                  </div>
                )}
                {municipality.urbanization_area && (
                  <div className="space-y-1">
                    <dt className="text-xs font-semibold text-primary uppercase tracking-wider">Verstädterungsgebiet</dt>
                    <dd className="text-text-inverse">
                      {municipality.urbanization_area.name || municipality.urbanization_area.code}
                      {municipality.urbanization_area.description && (
                        <span className="text-text-inverse/60 text-xs block">{municipality.urbanization_area.description}</span>
                      )}
                    </dd>
                  </div>
                )}
                {municipality.municipality_type_classification && (
                  <div className="space-y-1">
                    <dt className="text-xs font-semibold text-primary uppercase tracking-wider">Gemeindetyp</dt>
                    <dd className="text-text-inverse">
                      {municipality.municipality_type_classification.name || municipality.municipality_type_classification.code}
                      {municipality.municipality_type_classification.description && (
                        <span className="text-text-inverse/60 text-xs block">{municipality.municipality_type_classification.description}</span>
                      )}
                    </dd>
                  </div>
                )}
                {municipality.urban_rural_type && (
                  <div className="space-y-1">
                    <dt className="text-xs font-semibold text-primary uppercase tracking-wider">Stadt-/Landtyp</dt>
                    <dd className="text-text-inverse">
                      {municipality.urban_rural_type.name || municipality.urban_rural_type.code}
                      {municipality.urban_rural_type.description && (
                        <span className="text-text-inverse/60 text-xs block">{municipality.urban_rural_type.description}</span>
                      )}
                    </dd>
                  </div>
                )}
                {municipality.centrality_type && (
                  <div className="space-y-1">
                    <dt className="text-xs font-semibold text-primary uppercase tracking-wider">Zentralitätstyp</dt>
                    <dd className="text-text-inverse">
                      {municipality.centrality_type.name || municipality.centrality_type.code}
                      {municipality.centrality_type.description && (
                        <span className="text-text-inverse/60 text-xs block">{municipality.centrality_type.description}</span>
                      )}
                    </dd>
                  </div>
                )}
              </dl>
            </>
          )}
        </div>

        {/* Rechte Spalte: Aktionen (oben) + Verwaltungsebenen */}
        <div className="space-y-6">
          {/* Aktionsblock: hier macht der User seine Lage fertig */}
          <div className="card-accent">
            <h3 className="text-sm font-semibold text-gut8-anthracite uppercase tracking-wider mb-3">Lage fertigstellen</h3>
            {/* Kopier-Funktion nur, wenn ein Prompt-Template angelegt UND aktiv ist
                (der Prompt kommt ausschließlich aus der DB, kein Code-Fallback). */}
            {(lageLoading || lageprompt?.prompt) && (
              <button
                onClick={handleCopyPrompt}
                disabled={!lageprompt?.prompt}
                className="btn-primary w-full inline-flex items-center justify-center gap-2 disabled:opacity-50"
                title="Fertigen KI-Prompt in die Zwischenablage kopieren"
              >
                {lageLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : promptCopied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <ClipboardCopy className="w-4 h-4" />
                )}
                {promptCopied ? 'Kopiert' : 'Lageprompt kopieren'}
              </button>
            )}
            <button
              onClick={() => lageprompt && printLagebeschreibung(lageprompt)}
              disabled={!lageprompt}
              className="mt-2 w-full inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-sm rounded-gut8 border border-border-gut8-medium text-gut8-anthracite hover:bg-primary-subtle transition-colors disabled:opacity-50"
            >
              <Printer className="w-4 h-4" /> Drucken
            </button>
            <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-snug text-text-muted">
              <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5 text-primary" />
              <span>Texte automatisch aus den Lagedaten erzeugt (ohne KI). Lageprompt in ein LLM geben für eine ausgefeilte Fassung — Zahlen &amp; Ratings bleiben identisch.</span>
            </p>
          </div>

          {/* Verwaltungsebenen Card */}
          <div className="card-secondary">
            <h3 className="text-xl font-bold text-text-inverse mb-4">Verwaltungsebenen</h3>

            {municipality.municipal_association && (
              <div className="mb-4 pb-4 border-b border-text-inverse/10">
                <h4 className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">Gemeindeverband</h4>
                <div>
                  <div className="text-lg font-semibold text-text-inverse">{municipality.municipal_association.name}</div>
                  <div className="text-xs text-text-inverse/60">ARS: {municipality.municipal_association.ars}</div>
                  {municipality.municipal_association.textkennzeichen && (
                    <div className="text-xs text-text-inverse/60">({getTextkennzeichenName(municipality.municipal_association.textkennzeichen)})</div>
                  )}
                </div>
              </div>
            )}

            {municipality.county && (
              <div className="mb-4 pb-4 border-b border-text-inverse/10">
                <h4 className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">Kreis</h4>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-lg font-semibold text-text-inverse">{municipality.county.name}</div>
                    <div className="text-xs text-text-inverse/60">AGS: {municipality.county.county_key}</div>
                    {municipality.county.textkennzeichen && (
                      <div className="text-xs text-text-inverse/60">({getTextkennzeichenName(municipality.county.textkennzeichen)})</div>
                    )}
                  </div>
                  <button
                    onClick={() => onNavigateTab('kreis')}
                    className="text-sm text-primary hover:text-secondary flex items-center transition-colors"
                  >
                    Details <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {municipality.county?.government_district && (
              <div className="mb-4 pb-4 border-b border-text-inverse/10">
                <h4 className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">Regierungsbezirk</h4>
                <div>
                  <div className="text-lg font-semibold text-text-inverse">{municipality.county.government_district.name}</div>
                  <div className="text-xs text-text-inverse/60">AGS: {municipality.county.government_district.ags}</div>
                  {municipality.county.government_district.administrative_seat && (
                    <div className="text-xs text-text-inverse/60">Sitz: {municipality.county.government_district.administrative_seat}</div>
                  )}
                </div>
              </div>
            )}

            {municipality.state && (
              <div className="mb-4 pb-4 border-b border-text-inverse/10">
                <h4 className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">Bundesland</h4>
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-lg font-semibold text-text-inverse">{municipality.state.name}</div>
                    <div className="text-xs text-text-inverse/60">Code: {municipality.state.code}</div>
                  </div>
                  <button
                    onClick={() => onNavigateTab('bundesland')}
                    className="text-sm text-primary hover:text-secondary flex items-center transition-colors"
                  >
                    Details <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            <div>
              <h4 className="text-xs font-semibold text-primary uppercase tracking-wider mb-2">Bundesebene</h4>
              <div className="flex justify-between items-center">
                <div>
                  <div className="text-lg font-semibold text-text-inverse">Deutschland</div>
                  <div className="text-xs text-text-inverse/60">Bundesrepublik</div>
                </div>
                <button
                  onClick={() => onNavigateTab('bund')}
                  className="text-sm text-primary hover:text-secondary flex items-center transition-colors"
                >
                  Details <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lagebeschreibung: thematische Infokästchen (volle Breite) */}
      {lageLoading && (
        <div className="flex items-center justify-center py-12 text-text-muted">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Lagebeschreibung wird erstellt …
        </div>
      )}
      {lageprompt && <LageSectionBoxes data={lageprompt} />}
    </div>
  )
}
